import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Callout } from '@/components/ui/callout';
import { Card } from '@/components/ui/card/Card';
import { SectionLabel } from '@/components/ui/section-label';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuth } from '@/features/auth/hooks/useAuth';
import type { TraceabilityApiClient } from '../api/TraceabilityApiClient';
import type { TraceWriteRequest } from '../api/TraceabilityRequestType';
import type { Trace } from '../type/Tool';
import type { Process } from '../type/Process';
import { buildTracePayload } from '../hooks/useTraceFormBuilder';
import { useTraceSubmission } from '../hooks/useTraceSubmission';
import { processToFormValues } from '../utils/processToFormValues';
import { ProcessSelectedChip } from './ProcessSelectedChip';
import { ProcessFilteredFormSections } from './ProcessFilteredFormSections';
import { MaterialTab } from './FormTabs/MaterialTab';
import type { MaterialTabHandle } from './FormTabs/MaterialTab';
import { BreakTab } from './FormTabs/BreakTab';
import { flattenIsoTree } from '../data/isoMaterialTree';
import { useMachines } from '../hooks/useMachines';
import { ProcessMaterialPrefill } from './ProcessMaterialPrefill';
import { MachinePrefillBlock, MachineSelectBlock } from './ProcessMachineColumn';
import {
  ALL_MAPPABLE_KEYS,
  computeVisibleKeys,
  computePerPieceValues,
  fieldLabelKey,
} from '../utils/processFilteredFormUtils';

interface ProcessFilteredFormProps {
  process: Process;
  toolId: string;
  companyName: string;
  tracabilityApiClient: TraceabilityApiClient;
  onChange: () => void;
  onClear: () => void;
}

export function ProcessFilteredForm({
  process,
  toolId,
  companyName,
  tracabilityApiClient,
  onChange,
  onClear,
}: ProcessFilteredFormProps) {
  const { tokenService } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [validationError, setValidationError] = useState<string | null>(null);

  const [formValues, setFormValues] = useState<Record<string, unknown>>(() => {
    const seed = processToFormValues(process);
    if (!seed.operator) {
      const email = tokenService?.getUserMail();
      if (email) seed.operator = email;
    }
    return seed;
  });

  // Re-seed when process changes (Change action)
  useEffect(() => {
    setFormValues((prev) => {
      const seed = processToFormValues(process);
      if (!seed.operator) {
        const email = tokenService?.getUserMail();
        if (email) seed.operator = email;
      }
      // Preserve user-typed values for keys that are still missing in new process
      const merged: Record<string, unknown> = { ...seed };
      for (const key of ALL_MAPPABLE_KEYS) {
        if (!(key in seed) && key in prev) {
          merged[key] = prev[key];
        }
      }
      return merged;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [process]);

  const updateValue = (name: string, value: unknown) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
    setValidationError(null);
  };

  const visibleKeys = computeVisibleKeys(process);
  const hiddenCount = ALL_MAPPABLE_KEYS.length - visibleKeys.size;
  const isoOptions = useMemo(() => flattenIsoTree(t), [t]);

  const machines = useMachines(tracabilityApiClient);

  // When exactly one machine is pre-selected, the MachinePrefillBlock renders display-only
  // and never calls updateValue. We must inject the full Machine object into formValues
  // so buildTracePayload can send trace.machine to the backend.
  useEffect(() => {
    if (process.machineIds.length !== 1 || machines.length === 0) return;
    const found = machines.find((m) => m.id === process.machineIds[0]) ?? null;
    if (found) {
      setFormValues((prev) => ({ ...prev, machine: found }));
    }
  }, [process.machineIds, machines]);

  const isoCategoryLabel = (path: string) =>
    isoOptions.find((o) => o.value === path)?.label ?? path;
  const materialRef = useRef<MaterialTabHandle>(null);
  const breakRef = useRef<{ validate: () => boolean }>(null);
  const trace = useRef<TraceWriteRequest>({ trace: {} as Trace });
  const { execute, cancel, retry, result, formSubmitted, setFormSubmitted } = useTraceSubmission(
    tracabilityApiClient,
    trace
  );

  // First matching visible control wins: the plain input, a select trigger (machine), or a
  // DurationInput's hours sub-input (its main input is type="hidden", not focusable).
  const focusFirstMissingField = (root: HTMLElement, key: string) => {
    const el = root.querySelector<HTMLElement>(
      `input[name="${key}"]:not([type="hidden"]), [role="combobox"][id="${key}-select"], input[name="${key}-hours"]`
    );
    if (!el) return;
    // Optional call: jsdom (tests) doesn't implement scrollIntoView.
    el.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    el.focus({ preventScroll: true });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const matOk = materialRef.current?.validate() ?? true;
    const breakOk = breakRef.current?.validate() ?? true;
    if (!matOk || !breakOk) return;

    // Validate all visible fields are filled. Machine sits outside visibleKeys
    // (own column) but is backend-required — check it too, or an empty machine
    // only fails server-side ("trace.machine should not be empty").
    const missing = Array.from(visibleKeys).filter((key) => {
      const val = formValues[key];
      return val === null || val === undefined || val === '';
    });
    if (formValues.machine === null || formValues.machine === undefined) {
      missing.push('machine');
    }
    if (missing.length > 0) {
      // Name every missing field with the label the operator sees, and bring the
      // first one into view + focus (user request 2026-09-08).
      setValidationError(
        t('traceability.form.missingFieldsNamed', {
          fields: missing.map((key) => t(fieldLabelKey(key))).join(', '),
        })
      );
      focusFirstMissingField(e.currentTarget as HTMLElement, missing[0]);
      return;
    }
    setValidationError(null);

    setFormSubmitted(true);

    // Compute per-piece values: process stores per-piece rates; multiply by machinedPieces
    const pieces = formValues.pieces != null ? Number(formValues.pieces) : 0;
    const computedValues = computePerPieceValues(process, pieces);
    // Duration-less process: the operator was asked for duration_piece
    // (forced-visible); buildTracePayload sends it as pieceMachiningDuration,
    // which satisfies the backend's at-least-one-duration rule on its own.

    // When the process pre-fills material, inject material/materialLot into the payload
    // since processToFormValues deliberately omits them.
    const resolvedValues = {
      ...formValues,
      ...computedValues,
      material: process.material !== null ? process.material : formValues.material,
      materialLot: process.materialLot,
    };

    trace.current = buildTracePayload(resolvedValues, toolId, companyName);
    void execute();
  };

  if (formSubmitted && !result) {
    return (
      <Callout
        tone="neutral"
        loading
        action={
          <Button
            type="button"
            variant="outline"
            size="lg"
            className="w-full gap-2"
            onClick={() => { cancel(); setFormSubmitted(false); }}
          >
            {t('traceability.form.cancel')}
          </Button>
        }
      >
        {t('traceability.form.sending')}
      </Callout>
    );
  }

  if (formSubmitted && result?.ok === false) {
    return (
      <Callout
        tone="error"
        action={
          <>
            <Button
              type="button"
              variant="default"
              size="lg"
              className="flex-1 gap-2 sm:flex-none"
              onClick={retry}
            >
              {t('traceability.form.retry')}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="lg"
              className="flex-1 gap-2 sm:flex-none"
              onClick={() => navigate('/')}
            >
              {t('traceability.form.backHome')}
            </Button>
          </>
        }
      >
        {result.message ?? result.name ?? t('traceability.form.errorDefault')}
      </Callout>
    );
  }

  if (formSubmitted && result?.ok) {
    return (
      <Callout
        tone="success"
        action={
          <Button
            type="button"
            variant="default"
            size="lg"
            className="w-full gap-2"
            onClick={() => navigate('/traceability/scan')}
          >
            {t('traceability.form.newScan')}
          </Button>
        }
      >
        {t('traceability.form.success')}
      </Callout>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {validationError && <Callout tone="error">{validationError}</Callout>}
      <ProcessSelectedChip
        process={process}
        hiddenCount={hiddenCount}
        onChange={onChange}
        onClear={onClear}
      />

      <ProcessFilteredFormSections
        visibleKeys={visibleKeys}
        mandatoryFields={process.mandatoryFields}
        formValues={formValues}
        onChange={updateValue}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <SectionLabel className="mb-3">
            {t('traceability.processSelection.sections.material')}
          </SectionLabel>
          {process.material !== null ? (
            <ProcessMaterialPrefill
              material={process.material}
              lot={process.materialLot}
              isoCategoryLabel={isoCategoryLabel}
            />
          ) : (
            <MaterialTab
              ref={materialRef}
              values={formValues}
              onChange={updateValue}
              traceabilityApiClient={tracabilityApiClient}
            />
          )}
        </Card>
        <Card>
          <SectionLabel className="mb-3">
            {t('traceability.processSelection.sections.machine')}
          </SectionLabel>
          {process.machineIds.length === 1 ? (
            <MachinePrefillBlock
              machine={machines.find((m) => m.id === process.machineIds[0]) ?? null}
              t={t}
            />
          ) : (
            <MachineSelectBlock
              machines={
                process.machineIds.length > 1
                  ? machines.filter((m) => m.id !== undefined && process.machineIds.includes(m.id!))
                  : machines
              }
              value={formValues.machineId}
              onChange={(m) => { updateValue('machine', m); updateValue('machineId', m?.id ?? null); }}
              t={t}
            />
          )}
        </Card>
      </div>

      <Card>
        <SectionLabel className="mb-3">
          {t('traceability.processSelection.sections.toolState')}
        </SectionLabel>
        <BreakTab ref={breakRef} values={formValues} onChange={updateValue} />
      </Card>

      <Button type="submit" size="lg" className="w-full">
        {t('traceability.form.submit')}
      </Button>
    </form>
  );
}
